// externals
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

// libraries
import { ItemRetentionConfirmationModal, useHasRetainableItems } from '@makemydeal/dr-activities-change-vehicle';
import type { RetentionDecisions } from '@makemydeal/dr-activities-change-vehicle';
import * as changeVehicleRetentionActionCreators from '@makemydeal/dr-dash-store/actions/changeVehicleRetentionActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as programActionCreators from '@makemydeal/dr-dash-store/actions/programActionCreators';
import * as dealHistorySelectors from '@makemydeal/dr-dash-store/selectors/dealHistorySelectors';
import * as pendingDealerReviewSelectors from '@makemydeal/dr-dash-store/selectors/pendingDealerReviewSelectors';

const EMPTY_VEHICLE = { year: 0, make: '', model: '', trim: '' };

export const PendingReviewRetentionScreen = () => {
    const dispatch = useDispatch();

    const preChangeVehicle = useSelector(pendingDealerReviewSelectors.getPreChangeVehicleInfo);
    const postChangeVehicle = useSelector(pendingDealerReviewSelectors.getPostChangeVehicleInfo);
    const hasHistorySettled = useSelector(dealHistorySelectors.hasDealHistorySettled);
    const hasRetainableItems = useHasRetainableItems();
    const theme = useInterstateTheme();

    const [isDismissed, setIsDismissed] = useState(false);

    const isAwaitingHistory = preChangeVehicle === undefined && !hasHistorySettled;

    useEffect(() => {
        if (isAwaitingHistory || isDismissed) {
            return;
        }
        if (!hasRetainableItems || preChangeVehicle === undefined) {
            dispatch(offerActionCreators.resolvePendingDealerReviewPayment());
        }
    }, [isAwaitingHistory, isDismissed, hasRetainableItems, preChangeVehicle, dispatch]);

    const handleProceed = useCallback(
        (retentionDecisions: RetentionDecisions) => {
            setIsDismissed(true);
            dispatch(changeVehicleRetentionActionCreators.applyVehicleSwitchRetention(retentionDecisions));
            dispatch(offerActionCreators.resolvePendingDealerReviewPayment());
        },
        [dispatch]
    );

    const handleProceedAndOpenPrograms = useCallback(
        (retentionDecisions: RetentionDecisions) => {
            handleProceed(retentionDecisions);
            dispatch(programActionCreators.openProgramsSlideOut());
        },
        [dispatch, handleProceed]
    );

    if (isAwaitingHistory || isDismissed || !hasRetainableItems || preChangeVehicle === undefined) {
        return null;
    }

    return (
        <ThemeProvider theme={theme}>
            <ItemRetentionConfirmationModal
                show
                modalId="pending-review-retention-modal"
                allowDismiss={false}
                proceedLabel="Continue"
                currentVehicle={preChangeVehicle}
                newVehicle={postChangeVehicle ?? EMPTY_VEHICLE}
                onProceed={handleProceed}
                onProceedAndOpenPrograms={handleProceedAndOpenPrograms}
            />
        </ThemeProvider>
    );
};
